// 拠点間の在庫移動 — 新規作成・出庫は Business。降格後も履歴と解決操作は使える。
// 下書きを作る（在庫は動かない）→ 出庫（移動元から引く）→ 入庫（移動先へ足す・分納可）。
// 出庫・入庫はどちらも Shopify の在庫へ即時反映されるため window.confirm を挟む
// （在庫調整・棚卸しと同じ idiom・Polaris モーダルを増やさない）。
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Layout,
  Page,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { listAdjustableVariants } from "../api/inventory_adjustments";
import { ApiError } from "../api/client";
import {
  TRANSFER_STATUS_LABELS,
  cancelInventoryTransfer,
  createInventoryTransfer,
  getInventoryTransfer,
  listInventoryTransfers,
  receiveInventoryTransfer,
  shipInventoryTransfer,
} from "../api/inventory_transfers";
import type { TransferItem, TransferStatus } from "../api/inventory_transfers";
import { listLocations } from "../api/locations";
import { getPlanPreview } from "../api/plan_previews";
import { toLocationSelectOptions } from "../utils/locationOptions";
import { PlanDataPreviewGate } from "../components/PlanDataPreviewGate";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { usePlan } from "../hooks/usePlan";
import { useVerifiedShopGeneration } from "../ShopGenerationBoundary";

import { t } from "../lib/i18n";
import { parseQuantityInput } from "../utils/quantityInput";
import {
  differingRecordedProductTitle,
  displayVariantTitle,
  hasCurrentProductTitleProjection,
  merchantSafeRecordedProductTitle,
  projectedProductTitle,
} from "../utils/productTitles";
import { formatDateTime } from "../utils/date";
import {
  idempotencyAttempt,
  clearPersistentIdempotencyAttempt,
  persistentIdempotencyAttempt,
  type IdempotencyAttempt,
  type PersistentIdempotencyAttempt,
} from "../utils/idempotency";

const TRANSFERS_KEY = ["inventory-transfers"];
const TRANSFER_CREATE_ATTEMPT_SCOPE = "inventory-transfer:create";
const TRANSFER_SHIP_ATTEMPT_SCOPE = "inventory-transfer:ship";
const TRANSFER_RECEIVE_ATTEMPT_SCOPE = "inventory-transfer:receive";
const VARIANT_SEARCH_DEBOUNCE_MS = 300;
const VARIANT_SEARCH_LIMIT = 100;

interface DraftLine {
  shopify_variant_id: string;
  label: string;
  qty: number;
}

function isDefinitiveClientFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  // API契約上、副作用前の入力拒否と断定できるstatusだけをclearする。
  // 認証・認可・競合・rate-limit・未分類4xxは結果が曖昧なので保持する。
  return [400, 404, 422].includes(error.status);
}

function statusTone(
  status: TransferStatus,
): "info" | "attention" | "success" | undefined {
  if (status === "in_transit") return "attention";
  if (status === "received") return "success";
  if (status === "draft") return "info";
  return undefined;
}

function variantLabel(v: {
  product_title: string | null;
  title: string | null;
  sku: string | null;
}): string {
  const name = displayVariantTitle(v.product_title, v.title);
  const sku = v.sku ? `（SKU: ${v.sku}）` : "";
  return `${name || t("(名称未設定)")}${sku}`;
}

function transferItemLabel(item: TransferItem): string {
  const hasCurrent = hasCurrentProductTitleProjection(item);
  const safeRecorded = merchantSafeRecordedProductTitle(item);
  if (!hasCurrent) {
    const name = safeRecorded ?? item.sku ?? item.shopify_variant_id;
    const sku = item.sku && safeRecorded ? `（SKU: ${item.sku}）` : "";
    return `${name}${sku}`;
  }
  const current = projectedProductTitle(item) ?? item.shopify_variant_id;
  const sku = item.sku ? `（SKU: ${item.sku}）` : "";
  const recorded = differingRecordedProductTitle(item);
  const recordedLabel = recorded
    ? ` [${t("保存時の商品バリエーション名")}: ${recorded}]`
    : "";
  return `${current}${sku}${recordedLabel}`;
}

export function InventoryTransfers() {
  const queryClient = useQueryClient();
  const { shopGeneration } = useVerifiedShopGeneration();
  const plan = usePlan();
  const canStartTransfer =
    !plan.isLoading &&
    !plan.isError &&
    plan.isProductLimitExceeded !== true &&
    plan.allows("inventory_transfer");
  const [sourceId, setSourceId] = useState("");
  const [destId, setDestId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [variantSearch, setVariantSearch] = useState("");
  const [debouncedVariantSearch, setDebouncedVariantSearch] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [receiptQty, setReceiptQty] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const cancelAttempts = useRef<Record<string, IdempotencyAttempt>>({});
  const writeAttempts = useRef<
    Record<string, PersistentIdempotencyAttempt>
  >({});
  const createAttempt = useRef<PersistentIdempotencyAttempt | null>(null);

  const locationsQuery = useQuery({
    queryKey: ["locations"],
    queryFn: listLocations,
    enabled: canStartTransfer,
  });
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedVariantSearch(variantSearch.trim()),
      VARIANT_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [variantSearch]);
  const variantsQuery = useQuery({
    queryKey: ["adjustable-variants", "transfer", debouncedVariantSearch],
    queryFn: () =>
      listAdjustableVariants(debouncedVariantSearch, VARIANT_SEARCH_LIMIT),
    // 打鍵中に Select が一瞬空になると選択が飛ぶ。前回結果を残す。
    placeholderData: (previous) => previous,
    enabled: canStartTransfer,
  });
  const transfersQuery = useQuery({
    queryKey: TRANSFERS_KEY,
    queryFn: listInventoryTransfers,
  });
  const openTransferQuery = useQuery({
    queryKey: ["inventory-transfer", openId],
    queryFn: () => getInventoryTransfer(openId as string),
    enabled: openId !== null,
  });

  const onMutationError = (err: unknown) => {
    setSuccessMessage(null);
    setErrorMessage(err instanceof Error ? err.message : String(err));
  };
  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: TRANSFERS_KEY });
    queryClient.invalidateQueries({ queryKey: ["inventory-transfer"] });
    queryClient.invalidateQueries({ queryKey: ["adjustable-variants"] });
  };

  const createMutation = useMutation({
    mutationFn: ({
      input,
      attempt,
    }: {
      input: Parameters<typeof createInventoryTransfer>[0];
      attempt: PersistentIdempotencyAttempt;
    }) => createInventoryTransfer(input, attempt.key),
    onSuccess: (_transfer, variables) => {
      clearPersistentIdempotencyAttempt(
        TRANSFER_CREATE_ATTEMPT_SCOPE,
        variables.attempt,
      );
      createAttempt.current = null;
      setSuccessMessage(
        t(
          "移動の下書きを作成しました。まだ在庫は動いていません — 実際に品物を出したら「出庫する」を押してください。",
        ),
      );
      setErrorMessage(null);
      setLines([]);
      setNote("");
      refreshAll();
    },
    onError: (error, variables) => {
      if (isDefinitiveClientFailure(error)) {
        clearPersistentIdempotencyAttempt(
          TRANSFER_CREATE_ATTEMPT_SCOPE,
          variables.attempt,
        );
        createAttempt.current = null;
      }
      onMutationError(error);
    },
  });
  const shipMutation = useMutation({
    mutationFn: ({
      id,
      attempt,
    }: {
      id: string;
      attempt: PersistentIdempotencyAttempt;
    }) => shipInventoryTransfer(id, attempt.key),
    onSuccess: (_transfer, input) => {
      clearPersistentIdempotencyAttempt(
        TRANSFER_SHIP_ATTEMPT_SCOPE,
        input.attempt,
      );
      delete writeAttempts.current[`ship:${input.id}`];
      setSuccessMessage(t("出庫しました。移動元の在庫から差し引いています。"));
      setErrorMessage(null);
      refreshAll();
    },
    onError: (error, input) => {
      if (isDefinitiveClientFailure(error)) {
        clearPersistentIdempotencyAttempt(
          TRANSFER_SHIP_ATTEMPT_SCOPE,
          input.attempt,
        );
        delete writeAttempts.current[`ship:${input.id}`];
      }
      onMutationError(error);
    },
  });
  const receiveMutation = useMutation({
    mutationFn: (input: {
      id: string;
      receipts: { shopify_variant_id: string; qty: number }[];
      attempt: PersistentIdempotencyAttempt;
    }) => receiveInventoryTransfer(input.id, input.receipts, input.attempt.key),
    onSuccess: (transfer, input) => {
      clearPersistentIdempotencyAttempt(
        TRANSFER_RECEIVE_ATTEMPT_SCOPE,
        input.attempt,
      );
      delete writeAttempts.current[`receive:${input.id}`];
      setSuccessMessage(
        transfer.status === "received"
          ? t("入庫を記録しました。移動先の在庫へ全量を反映しました。")
          : t("入庫を記録しました。残りはあとから受領できます。"),
      );
      setErrorMessage(null);
      setReceiptQty({});
      refreshAll();
    },
    onError: (error, input) => {
      if (isDefinitiveClientFailure(error)) {
        clearPersistentIdempotencyAttempt(
          TRANSFER_RECEIVE_ATTEMPT_SCOPE,
          input.attempt,
        );
        delete writeAttempts.current[`receive:${input.id}`];
      }
      onMutationError(error);
    },
  });
  const cancelMutation = useMutation({
    mutationFn: ({ id, key }: { id: string; key: string }) =>
      cancelInventoryTransfer(id, key),
    onSuccess: (_transfer, input) => {
      delete cancelAttempts.current[`cancel:${input.id}`];
      setSuccessMessage(t("移動を取り消しました。"));
      setErrorMessage(null);
      refreshAll();
    },
    onError: onMutationError,
  });

  const locations = locationsQuery.data ?? [];
  const variants = variantsQuery.data ?? [];
  const transfers = transfersQuery.data ?? [];
  const transferActionPending =
    shipMutation.isPending ||
    receiveMutation.isPending ||
    cancelMutation.isPending;

  const locationOptions = [
    { label: t("拠点を選択してください"), value: "", disabled: false },
    ...toLocationSelectOptions(locations),
  ];
  const variantOptions = [
    { label: t("商品を選択してください"), value: "" },
    ...variants.map((v) => ({
      label: variantLabel(v),
      value: v.shopify_variant_id,
    })),
  ];

  const qtyInput = parseQuantityInput(qty);
  const parsedQty = qtyInput.kind === "value" ? qtyInput.value : Number.NaN;
  const canAddLine =
    variantId !== "" && qtyInput.kind === "value" && parsedQty > 0;
  const sameLocation = sourceId !== "" && sourceId === destId;
  const canCreate =
    sourceId !== "" &&
    destId !== "" &&
    !sameLocation &&
    lines.length > 0 &&
    !createMutation.isPending;

  const handleAddLine = () => {
    const variant = variants.find((v) => v.shopify_variant_id === variantId);
    if (!variant || !canAddLine) return;
    setLines((prev) => [
      // 同じ商品は1行にまとめる（backend も重複行を 422 で拒否する）
      ...prev.filter(
        (l) => l.shopify_variant_id !== variant.shopify_variant_id,
      ),
      {
        shopify_variant_id: variant.shopify_variant_id,
        label: variantLabel(variant),
        qty: parsedQty,
      },
    ]);
    setVariantId("");
    setQty("");
  };

  const handleCreate = () => {
    if (!canCreate) return;
    const input = {
      source_location_id: sourceId,
      dest_location_id: destId,
      note: note.trim() === "" ? undefined : note.trim(),
      items: lines.map((l) => ({
        shopify_variant_id: l.shopify_variant_id,
        qty: l.qty,
      })),
    };
    const attempt = persistentIdempotencyAttempt({
      scope: TRANSFER_CREATE_ATTEMPT_SCOPE,
      shopGeneration,
      previous: createAttempt.current,
      fingerprint: JSON.stringify(input),
    });
    createAttempt.current = attempt;
    createMutation.mutate({ input, attempt });
  };

  const handleShip = (id: string, name: string) => {
    if (transferActionPending || !canStartTransfer) return;
    const ok = window.confirm(
      t(
        "{location} の在庫から移動数量を差し引きます。Shopify の在庫に即時反映されます。よろしいですか？",
        { location: name },
      ),
    );
    if (!ok) return;
    const attemptName = `ship:${id}`;
    const attempt = persistentIdempotencyAttempt({
      scope: TRANSFER_SHIP_ATTEMPT_SCOPE,
      shopGeneration,
      previous: writeAttempts.current[attemptName] ?? null,
      fingerprint: JSON.stringify({ id, action: "ship" }),
    });
    writeAttempts.current[attemptName] = attempt;
    shipMutation.mutate({ id, attempt });
  };

  const handleCancel = (id: string) => {
    if (transferActionPending) return;
    const ok = window.confirm(
      t(
        "この移動を取り消します。まだ出庫していないため在庫は動きません。よろしいですか？",
      ),
    );
    if (!ok) return;
    const attemptName = `cancel:${id}`;
    const attempt = idempotencyAttempt(
      cancelAttempts.current[attemptName] ?? null,
      JSON.stringify({ id, action: "cancel" }),
    );
    cancelAttempts.current[attemptName] = attempt;
    cancelMutation.mutate({ id, key: attempt.key });
  };

  const openTransfer = openTransferQuery.data ?? null;
  const receipts = (openTransfer?.items ?? [])
    .map((item) => ({
      shopify_variant_id: item.shopify_variant_id,
      parsed: parseQuantityInput(receiptQty[item.shopify_variant_id] ?? ""),
    }))
    .flatMap((r) =>
      r.parsed.kind === "value" && r.parsed.value > 0
        ? [{ shopify_variant_id: r.shopify_variant_id, qty: r.parsed.value }]
        : [],
    );

  const handleReceive = () => {
    if (!openTransfer || receipts.length === 0 || transferActionPending) return;
    const ok = window.confirm(
      `${openTransfer.dest_location_name ?? t("移動先")} の在庫へ受領数量を加算します。` +
        t("Shopify の在庫に即時反映されます。よろしいですか？"),
    );
    if (!ok) return;
    const attemptName = `receive:${openTransfer.id}`;
    const attempt = persistentIdempotencyAttempt({
      scope: TRANSFER_RECEIVE_ATTEMPT_SCOPE,
      shopGeneration,
      previous: writeAttempts.current[attemptName] ?? null,
      fingerprint: JSON.stringify({
        id: openTransfer.id,
        action: "receive",
        receipts,
      }),
    });
    writeAttempts.current[attemptName] = attempt;
    receiveMutation.mutate({
      id: openTransfer.id,
      receipts,
      attempt,
    });
  };

  const transferRows = transfers.map((tr) => [
    `${tr.source_location_name ?? "—"} → ${tr.dest_location_name ?? "—"}`,
    t(TRANSFER_STATUS_LABELS[tr.status]),
    String(tr.item_count),
    formatDateTime(tr.shipped_at),
    tr.note ?? "",
  ]);

  return (
    <Page title={t("拠点間の在庫移動")}>
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {successMessage && (
              <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
                {successMessage}
              </Banner>
            )}
            {errorMessage && (
              <Banner
                tone="critical"
                title={t("移動の操作に失敗しました")}
                onDismiss={() => setErrorMessage(null)}
              >
                {errorMessage}
              </Banner>
            )}
            {(transfersQuery.isError ||
              openTransferQuery.isError ||
              (canStartTransfer &&
                (locationsQuery.isError || variantsQuery.isError))) && (
              <QueryErrorBanner
                title={t("拠点間移動の情報の取得に失敗しました")}
                onRetry={() => {
                  if (canStartTransfer) {
                    locationsQuery.refetch();
                    variantsQuery.refetch();
                  }
                  transfersQuery.refetch();
                  if (openId !== null) openTransferQuery.refetch();
                }}
              />
            )}
            <PlanDataPreviewGate
              feature="inventory_transfer"
              featureName={t("移動の作成")}
              previewQueryKey={["plan-preview", "transfer-candidates"]}
              previewQueryFn={async () => [
                await getPlanPreview("transfer_candidates"),
              ]}
              sections={[
                {
                  dataset: "transfer_candidates",
                  title: t("移動対象の商品"),
                  columns: [
                    { key: "product", label: t("商品") },
                    { key: "sku", label: "SKU" },
                    { key: "available", label: t("在庫") },
                    { key: "transfer_qty", label: t("移動数量") },
                  ],
                },
              ]}
            >
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    {t("移動をつくる")}
                  </Text>
                  {/* 🔴 純正との混乱防止（UI 規約）— 純正「転送」を作るわけではないこと・
                      二重計上の注意・履歴の範囲の3点を必ず出す。 */}
                  <Text as="p" tone="subdued">
                    {t(
                      "拠点から拠点へ品物を動かした記録を残します。出庫すると移動元の在庫が減り、入庫すると移動先の在庫が増えます— Shopify 管理画面で両方の拠点の数量を手で直すのと同じ効果です。 Shopify の「転送（Transfer）」は作成しません（作成した転送は途中で取り消せないため）。同じ移動を Shopify の転送でも行うと二重に計上されるため、どちらか一方で管理してください。",
                    )}
                  </Text>
                  <Select
                    label={t("移動元の拠点")}
                    options={locationOptions}
                    value={sourceId}
                    onChange={setSourceId}
                    disabled={locationsQuery.isLoading}
                  />
                  <Select
                    label={t("移動先の拠点")}
                    options={locationOptions}
                    value={destId}
                    onChange={setDestId}
                    disabled={locationsQuery.isLoading}
                    error={
                      sameLocation
                        ? t("移動元と移動先には別の拠点を指定してください。")
                        : undefined
                    }
                  />
                  <TextField
                    label={t("商品を検索（SKU・商品名・Shopify バリアントID）")}
                    value={variantSearch}
                    onChange={(value) => {
                      setVariantSearch(value);
                      setVariantId("");
                    }}
                    autoComplete="off"
                    maxLength={100}
                    clearButton
                    onClearButtonClick={() => {
                      setVariantSearch("");
                      setVariantId("");
                    }}
                    helpText={t(
                      "全商品をサーバーから検索します。候補は最大100件です。見つからない場合は SKU またはバリアントIDを入力してください。",
                    )}
                  />
                  <Select
                    label={t("商品")}
                    options={variantOptions}
                    value={variantId}
                    onChange={setVariantId}
                    disabled={variantsQuery.isLoading}
                  />
                  <TextField
                    label={t("移動する数量")}
                    type="integer"
                    value={qty}
                    onChange={setQty}
                    autoComplete="off"
                    helpText={t("1以上で入力してください。")}
                  />
                  <div>
                    <Button onClick={handleAddLine} disabled={!canAddLine}>
                      {t("明細に追加")}
                    </Button>
                  </div>
                  {lines.length > 0 && (
                    <DataTable
                      columnContentTypes={["text", "numeric"]}
                      headings={[t("商品"), t("数量")]}
                      rows={lines.map((l) => [l.label, String(l.qty)])}
                    />
                  )}
                  <TextField
                    label={t("メモ（任意）")}
                    value={note}
                    onChange={setNote}
                    autoComplete="off"
                    maxLength={2000}
                  />
                  <div>
                    <Button
                      variant="primary"
                      onClick={handleCreate}
                      disabled={!canCreate}
                      loading={createMutation.isPending}
                    >
                      {t("移動を作成")}
                    </Button>
                  </div>
                </BlockStack>
              </Card>
            </PlanDataPreviewGate>

            {transfers
              .filter(
                (tr) => tr.status === "draft" || tr.status === "in_transit",
              )
              .map((tr) => (
                <Card key={tr.id}>
                  <BlockStack gap="300">
                    <InlineStack gap="200" align="start" blockAlign="center">
                      <Text as="h2" variant="headingMd">
                        {tr.source_location_name ?? "—"} →{" "}
                        {tr.dest_location_name ?? "—"}
                      </Text>
                      <Badge tone={statusTone(tr.status)}>
                        {t(TRANSFER_STATUS_LABELS[tr.status])}
                      </Badge>
                    </InlineStack>
                    {tr.status === "draft" && (
                      <BlockStack gap="200">
                        {!canStartTransfer && (
                          <Text as="p" tone="subdued">
                            {t(
                              "出庫には Business プランが必要です。下書きの取り消しは現在のプランでも行えます。",
                            )}
                          </Text>
                        )}
                        <InlineStack gap="200">
                          {canStartTransfer && (
                            <Button
                              variant="primary"
                              onClick={() =>
                                handleShip(
                                  tr.id,
                                  tr.source_location_name ?? t("移動元"),
                                )
                              }
                              disabled={transferActionPending}
                              loading={
                                shipMutation.isPending &&
                                shipMutation.variables?.id === tr.id
                              }
                            >
                              {t("出庫する")}
                            </Button>
                          )}
                          <Button
                            tone="critical"
                            onClick={() => handleCancel(tr.id)}
                            disabled={transferActionPending}
                            loading={
                              cancelMutation.isPending &&
                              cancelMutation.variables?.id === tr.id
                            }
                          >
                            {t("取り消す")}
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    )}
                    {tr.status === "in_transit" && (
                      <BlockStack gap="300">
                        <Text as="p" tone="subdued">
                          {t(
                            "出庫済みです。届いた数量を入力して入庫を記録してください（分けて届いた分だけでも記録できます）。",
                          )}
                        </Text>
                        {openId === tr.id ? (
                          <BlockStack gap="300">
                            {(openTransfer?.items ?? []).map((item) => (
                              <TextField
                                key={item.shopify_variant_id}
                                label={t("{item} の受領数（残り {n}）", {
                                  item: transferItemLabel(item),
                                  n: item.remaining_qty,
                                })}
                                type="integer"
                                value={
                                  receiptQty[item.shopify_variant_id] ?? ""
                                }
                                onChange={(value) =>
                                  setReceiptQty((prev) => ({
                                    ...prev,
                                    [item.shopify_variant_id]: value,
                                  }))
                                }
                                autoComplete="off"
                                disabled={transferActionPending}
                              />
                            ))}
                            <div>
                              <Button
                                variant="primary"
                                onClick={handleReceive}
                                disabled={
                                  receipts.length === 0 || transferActionPending
                                }
                                loading={
                                  receiveMutation.isPending &&
                                  receiveMutation.variables?.id === tr.id
                                }
                              >
                                {t("入庫を記録する")}
                              </Button>
                            </div>
                          </BlockStack>
                        ) : (
                          <div>
                            <Button
                              onClick={() => setOpenId(tr.id)}
                              disabled={transferActionPending}
                            >
                              {t("受領する")}
                            </Button>
                          </div>
                        )}
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
              ))}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t("移動の履歴")}
                </Text>
                <Text as="p" tone="subdued">
                  {t(
                    "この履歴は UreyukiBox から行った移動のみです。Shopify 管理画面で直接動かした在庫はここには並びません（在庫数そのものには同期で反映されます）。",
                  )}
                </Text>
                {transferRows.length === 0 ? (
                  <Text as="p" tone="subdued">
                    {t("移動の履歴はまだありません。")}
                  </Text>
                ) : (
                  <DataTable
                    columnContentTypes={[
                      "text",
                      "text",
                      "numeric",
                      "text",
                      "text",
                    ]}
                    headings={[
                      t("経路"),
                      t("状態"),
                      t("品目数"),
                      t("出庫日時"),
                      t("メモ"),
                    ]}
                    rows={transferRows}
                  />
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
